<template>
  <div>
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <router-link to="/tenants/claimList" class="small d-inline-block mb-2">&larr; Claims</router-link>
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">
              {{ obj.id ? `Claim ${obj.claimNo || ""}` : "New claim" }}
              <CBadge
                v-if="obj.claimStateDescription"
                :color="hrStatusColor('claim', obj.claimStateDescription)"
                class="ml-2 align-middle"
              >
                {{ hrStatusLabel('claim', obj.claimStateDescription) }}
              </CBadge>
            </h1>
            <p class="text-muted mb-0">{{ headerSubtitle }}</p>
          </CCol>
          <CCol md="6" class="d-flex flex-wrap justify-content-md-end align-items-center mt-3 mt-md-0">
            <CDropdown
              v-if="obj.id && computedClaimStates.length > 0"
              placement="bottom-end"
              toggler-text="Change status"
              color="light"
              class="mr-2 mb-2 mb-md-0"
            >
              <template v-if="computedClaimStates.length">
                <CDropdownItem
                  v-for="status in computedClaimStates"
                  :key="status.id"
                  @click="askChangeState(status)"
                >
                  {{ stateActionLabel(status.name) }}
                </CDropdownItem>
              </template>
            </CDropdown>
            <CButton color="light" class="mr-2 mb-2 mb-md-0" :disabled="saving" @click="cancel">Cancel</CButton>
            <CButton v-if="isEditable" color="primary" class="mb-2 mb-md-0" :disabled="saving" @click="submit">
              <CSpinner v-if="saving" size="sm" class="mr-1" />
              {{ obj.id ? "Save" : "Create claim" }}
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>
    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardBody>
            <CRow>
              <CCol sm="6" md="3" class="mb-3">
                <div class="small text-muted">Claim no.</div>
                <div class="text-monospace">{{ obj.claimNo || "—" }}</div>
              </CCol>
              <CCol sm="6" md="3" class="mb-3">
                <div class="small text-muted">Employee</div>
                <div>{{ (obj.profile && obj.profile.fullName) || "—" }}</div>
              </CCol>
              <CCol sm="6" md="3" class="mb-3">
                <div class="small text-muted">Date</div>
                <div>{{ formatDate(obj.date) || "—" }}</div>
              </CCol>
              <CCol sm="6" md="3" class="mb-3">
                <div class="small text-muted">Total</div>
                <div class="font-weight-bold">{{ money(grandTotal) }}</div>
              </CCol>
            </CRow>

          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardHeader><strong>Items</strong></CCardHeader>
          <CCardBody>
            <p v-if="needsAccounts" class="text-muted small">
              Choose the expense account for each item before you approve the claim.
            </p>
            <CDataTable :items="computedClaimItems" :fields="claimFields" responsive>
              <template #show_index="{ index }">
                <td class="py-2">
                  {{ index + 1 }}
                </td>
              </template>

              <template #show_item_description="{ item }">
                <td>
                  <strong>{{ item.name }}</strong>
                  <br />
                  <small>{{ item.description }}</small>
                </td>
              </template>
              <template #show_receipt="{ item }">
                <td class="py-2">
                  <div v-if="item.receipt && item.receipt.documentId">
                    <div v-if="item.receipt.document">
                      <div v-if="helper.isPdf(item.receipt.document)">
                        <iframe
                          :src="helper.getDocumentUrl(item.receipt.document)"
                          class="receipt-frame border rounded"
                          title="Receipt"
                        ></iframe>
                      </div>
                      <div v-else>
                        <CImg
                          :src="helper.getDocumentUrl(item.receipt.document)"
                          class="receipt-img border rounded"
                          alt="Receipt"
                        />
                      </div>
                    </div>
                    <a
                      :href="helper.getDocumentUrl(item.receipt.document)"
                      target="_blank"
                      class="d-block my-1"
                      >View receipt</a
                    >
                    <div class="small">
                      <div class="font-weight-bold">{{ item.receipt.companyName }}</div>
                      <div class="text-muted">{{ formatDate(item.receipt.date) }}</div>
                      <div>{{ money(item.receipt.totalAmount) }}</div>
                    </div>
                  </div>
                  <div v-else class="text-muted">No receipt</div>
                </td>
              </template>
              <template #show_account="{ item }">
                <td>
                  <CFormGroup>
                    <template #input>
                      <v-select
                        v-if="needsAccounts"
                        style="width: 100%"
                        v-model="item.chartAccount"
                        :label="`itemDisplay`"
                        :options="expenseChartOfAccountItems"
                        placeholder="Choose an account"
                      />
                      <span v-else>{{ (item.chartAccount && item.chartAccount.itemDisplay) || "—" }}</span>
                    </template>
                  </CFormGroup>
                  <small v-if="showAccountErrors && !(item.chartAccount && item.chartAccount.id)" class="text-danger">
                    Choose an expense account for this item.
                  </small>
                </td>
              </template>
              <!-- <template #show_item="{ item }">
                <td>
                  <CInput v-model="item.name" min="1"></CInput>
                </td>
              </template> -->
              <!-- <template #show_description="{ item }">
                <td>
                  <CTextarea
                    placeholder=""
                    v-model="item.description"
                    rows="1"
                  />
                </td>
              </template> -->
              <template #show_quantity="{ item }">
                <td>
                  <CInput v-model="item.quantity" min="1"></CInput>
                </td>
              </template>
              <template #show_unit="{ item }">
                <td>
                  <CInput v-model="item.unit" min="1"></CInput>
                </td>
              </template>
              <template #show_price="{ item }">
                <td>
                  <CInput v-model="item.amount" min="0" step="0.01"></CInput>
                </td>
              </template>
              <template #show_total="{ item }">
                <td class="text-right text-nowrap">
                  {{ money(getTotalItemPrice(item)) }}
                </td>
              </template>
              <template #show_remove="{ item }">
                <td class="py-2">
                  <CButton
                    color="primary"
                    variant="outline"
                    square
                    size="sm"
                    @click="onRemoveClaimItem(item)"
                  >
                    Remove
                  </CButton>
                </td>
              </template>

              <template #footer>
                <td colspan="5" class="text-right">
                  <strong>Total</strong>
                </td>
                <td class="text-right text-nowrap"><strong>{{ money(grandTotal) }}</strong></td>
              </template>
            </CDataTable>
          </CCardBody>
          <CCardFooter v-if="isEditable" class="d-flex justify-content-end">
            <CButton color="light" class="mr-2" :disabled="saving" @click="cancel">Cancel</CButton>
            <CButton color="primary" :disabled="saving" @click="submit">
              <CSpinner v-if="saving" size="sm" class="mr-1" /> Save
            </CButton>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
    <CModal
        :title="`Mark claim ${obj.claimNo || ''} as paid?`"
        :show.sync="chooseAccountPopup"
        :close-on-backdrop="!changingState"
        @update:show="onAccountPopupConfirmation"
      >
        <p>
          {{ money(grandTotal) }} will be recorded as paid to
          {{ (obj.profile && obj.profile.fullName) || "the employee" }} from the account you choose.
        </p>
        <CRow form>
          <CCol md="12">
            <label class="d-block">Paid from account *</label>
            <CFormGroup>
              <template #input>
                <v-select
                  style="width: 100%"
                  v-model="selectedChartAccount"
                  :label="`itemDisplay`"
                  :options="filteredChartAccounts"
                  placeholder="Choose an account"
                />
              </template>
            </CFormGroup>
          </CCol>
        </CRow>

        <template #footer>
          <CButton color="light" :disabled="changingState" @click="chooseAccountPopup = false"
            >Cancel</CButton
          >
          <CButton
            color="primary"
            :disabled="!selectedChartAccount || changingState"
            @click="confirmAccountSelection"
          >
            <CSpinner v-if="changingState" size="sm" class="mr-1" />
            Mark as paid
          </CButton>
        </template>
      </CModal>

    <!-- Status change confirmation -->
    <CModal :title="statusConfirmTitle" :show.sync="statusConfirmModal" :close-on-backdrop="!changingState">
      <p class="mb-0">{{ statusConfirmMessage }}</p>
      <template #footer>
        <CButton color="light" :disabled="changingState" @click="statusConfirmModal = false">
          {{ isPendingNegative ? "Keep it" : "Cancel" }}
        </CButton>
        <CButton :color="isPendingNegative ? 'danger' : 'primary'" :disabled="changingState" @click="proceedChangeState(null)">
          <CSpinner v-if="changingState" size="sm" class="mr-1" />
          {{ pendingStatusChange ? stateActionLabel(pendingStatusChange.name) : "" }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import ClaimApi from "@/lib/claimApi";
import ChartOfAccountApi from "../../lib/chartOfAccountApi";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { formatDate } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";
import { hrStatusMixin, hrStatusLabel } from "@/utils/hrStatusBadges";

const claimItems = [];
const claimFields = [
  {
    key: "show_index",
    label: "#",
    _style: "width:1%",
    sorter: false,
    filter: false,
  },
  // { key: "productName", label: "Item" },
  {
    key: "dateDisplay",
    label: "Date",
  },
  {
    key: "show_item_description",
    label: "Item",
  },
  // {
  //   key: "show_description",
  //   label: "Description",
  // },
  // {
  //   key: "show_quantity",
  //   label: "Quantity",
  //   _style: "width:100px",
  // },
  // {
  //   key: "show_unit",
  //   label: "Unit",
  //   _style: "width:100px",
  // },

  // {
  //   key: "show_price",
  //   label: "Price",
  //   _style: "width:100px",
  // },
  {
    key: "show_receipt",
    label: "Receipt",
    _style: "width:160px",
  },
  {
    key: "show_account",
    label: "Account",
    _style: "width:300px",
  },
  {
    key: "show_total",
    label: "Total",
    _style: "text-align:right",
  },

  // {
  //   key: "show_remove",
  //   _style: "width:1%",
  // },
];

export default {
  name: "Claim",
  components: {
    vSelect,
  },
  mixins: [hrStatusMixin],
  data: () => {
    return {
      chooseAccountPopup: false,
      selectedChartAccount: null,
      pendingStatusChange: null,
      filteredChartAccounts: [],

      chartOfAccountItems: [],
      expenseChartOfAccountItems: [],
      chartOfAccountApi: new ChartOfAccountApi(),
      helper,
      // Quotation Itm
      claimItems: claimItems.map((item, id) => {
        return { ...item, id };
      }),

      claimFields,

      claimStates: [],
      // selectedClaimState: null,
      organizationTypeList: [],
      saving: false,
      changingState: false,
      statusConfirmModal: false,
      showAccountErrors: false,
      obj: {
        profile: {
          fullName: "",
        },
        business: {
          name: "",
        },
      },
      submitted: false,
      api: new ClaimApi(),
    };
  },
  mounted() {
    var self = this;
    this.fetchChartOfAccount();

    this.fetchClaimStates();
    self.resetObj();
  },
  computed: {
    computedClaimStates() {
      let statuses = this.claimStates.filter(
        (status) => status.name != this.obj.claimStateDescription
      );

      if (this.obj.id == null) return [];

      if (this.obj.claimStateDescription === "Draft")
        return statuses.filter((status) => status.name == "Submit");
      if (this.obj.claimStateDescription === "Submit")
        return statuses.filter(
          (status) => status.name == "Approve" || status.name == "NotApprove"
        );
      if (this.obj.claimStateDescription === "Approve")
        return statuses.filter(
          (status) => status.name == "Paid" || status.name == "Cancelled"
        );
      else return [];
    },

    // computedChartAccountItems() {
    //   return this.chartOfAccountItems.filter(
    //     (a) => a.accountTypeDescription === "Expense"
    //   );

    //   // return this.chartOfAccountItems.map((item) => {
    //   //   return {
    //   //     ...item,
    //   //   };
    //   // });
    // },
    isEditable() {
      return ["Draft", "Submit"].includes(this.obj.claimStateDescription);
    },
    // Accounts are chosen while the claim is waiting for approval.
    needsAccounts() {
      return this.isEditable;
    },
    headerSubtitle() {
      const who = (this.obj.profile && this.obj.profile.fullName) || "";
      return who ? `${who} · ${this.money(this.grandTotal)}` : "Review the items and receipts, then approve or reject";
    },
    isPendingNegative() {
      return !!this.pendingStatusChange && ["NotApprove", "Cancelled"].includes(this.pendingStatusChange.name);
    },
    statusConfirmTitle() {
      if (!this.pendingStatusChange) return "";
      const no = this.obj.claimNo ? ` ${this.obj.claimNo}` : "";
      switch (this.pendingStatusChange.name) {
        case "Approve": return `Approve claim${no}?`;
        case "NotApprove": return `Reject claim${no}?`;
        case "Cancelled": return `Cancel claim${no}?`;
        case "Submit": return `Submit claim${no}?`;
        default: return `Mark claim${no} as ${hrStatusLabel("claim", this.pendingStatusChange.name).toLowerCase()}?`;
      }
    },
    statusConfirmMessage() {
      if (!this.pendingStatusChange) return "";
      const who = (this.obj.profile && this.obj.profile.fullName) || "the employee";
      const total = this.money(this.grandTotal);
      switch (this.pendingStatusChange.name) {
        case "Approve": return `The claim from ${who} for ${total} will be approved and posted to your accounts. You can mark it as paid after this.`;
        case "NotApprove": return `The claim from ${who} for ${total} will be marked as not approved and won't be paid.`;
        case "Cancelled": return `The claim from ${who} for ${total} will be cancelled and won't be paid.`;
        default: return `The claim from ${who} for ${total} will be marked as ${hrStatusLabel("claim", this.pendingStatusChange.name).toLowerCase()}.`;
      }
    },
    computedClaimItems() {
      return this.claimItems.map((item) => {
        return {
          ...item,
          dateDisplay: formatDate(item.date),
          // productName: item.product.name,
          // totalAmountPerItem: this.getTotalItemPrice(item),
        };
      });
    },
    grandTotal() {
      var total = 0;
      for (var i = 0; i < this.claimItems.length; i++) {
        var item = this.claimItems[i];
        total += (parseFloat(item.amount) || 0) * (parseFloat(item.quantity) || 0);
      }
      return total;
    },
  },
  methods: {
    formatDate,
    money(v) {
      return `RM ${formatMyr(v)}`;
    },
    stateActionLabel(name) {
      if (name === "Approve") return "Approve claim";
      if (name === "NotApprove") return "Reject claim";
      if (name === "Paid") return "Mark as paid";
      if (name === "Cancelled") return "Cancel claim";
      if (name === "Submit") return "Submit claim";
      return hrStatusLabel("claim", name);
    },
    askChangeState(item) {
      this.changeState(item);
    },
    changeState(item) {
      this.pendingStatusChange = item;
      this.selectedChartAccount = null; // Reset selected chart account
     if (item.name === "Paid") {
        this.filteredChartAccounts = this.chartOfAccountItems.filter(
          (a) => a.accountTypeDescription === "Asset"
        );
        // 🔍 Check historical expense data for this vendor
        this.api
          .getRecommendedChartAccount(this.obj.profileId, item.name)
          .then((response) => {
            const recommended = this.filteredChartAccounts.find(
              (a) => a.id === response.result
            );
            if (recommended) {
              this.selectedChartAccount = recommended;
            }
          })
          .finally(() => {
            this.chooseAccountPopup = true;
          });
        // this.chooseAccountPopup = true;
      } else {
        // Approving posts to the ledger, rejecting stops payment: always confirm.
        this.statusConfirmModal = true;
      }
    },
    onAccountPopupConfirmation(visible) {
      this.chooseAccountPopup = visible;
    },

    confirmAccountSelection() {
      this.proceedChangeState(this.selectedChartAccount);
      this.selectedChartAccountId = null;
    },

    proceedChangeState(account) {
      var self = this;
      self.obj.claimState = self.pendingStatusChange.id;
      let accountId = null;
      if (account != null) accountId = account.id;

      if (self.obj.id) {
        const name = self.pendingStatusChange.name;
        self.changingState = true;
        this.api
          .updateState(self.obj, accountId)
          .then((response) => {
            self.toast("Success", name === "NotApprove" ? "Claim rejected" : `Claim marked as ${hrStatusLabel("claim", name).toLowerCase()}`, "success");
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't change the claim status. ${helper.getErrorMessage(data)}`, "danger");
          })
          .finally(() => {
            this.chooseAccountPopup = false;
            this.statusConfirmModal = false;
            this.changingState = false;
            this.pendingStatusChange = null;
          });
      }
    },
    cancel() {
      this.$router.push({ path: "/tenants/ClaimList" });
    },

    fetchChartOfAccount() {
      var self = this;
      self.chartOfAccountApi
        .getListByCurrentBusiness()
        .then((response) => {
          self.chartOfAccountItems = response.result;
          self.expenseChartOfAccountItems = self.chartOfAccountItems.filter(
            (a) => a.accountTypeDescription === "Expense"
          );

          // self.resetObj();

          // self.chartAccountItemsLoaded = true;
        })
        .catch(({ data }) => {});
    },
    onRemoveClaimItem(item) {
      var self = this;
      if (self.computedClaimItems != null) {
        for (var i = 0; i < self.computedClaimItems.length; i++) {
          if (self.computedClaimItems[i].id === item.id) {
            self.computedClaimItems.splice(i, 1);
          }
        }
      }
    },
    generateGUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
          var r = (Math.random() * 16) | 0,
            v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }
      );
    },

    addNewItem() {
      this.computedClaimItems.push({
        id: this.generateGUID(),
        name: "",
        amount: 0,
        unit: "L.S.",
        quantity: 0,
        description: "",
      });
    },
    getTotalItemPrice(item) {
      try {
        return item.quantity * item.amount;
      } catch (error) {
        return 0;
      }
    },

    fetchClaimStates() {
      var self = this;
      self.api
        .getClaimState()
        .then((response) => {
          this.claimStates = response.result;

          // var obj = response.result;
          // this.claimStates = obj.map((state) => ({
          //   value: state.id,
          //   label: state.name,
          // }));

        })
        .catch(({ data }) => {
          self.toast("Error", `Couldn't load claim statuses. ${helper.getErrorMessage(data)}`, "danger");
        });
    },
    resetObj() {
      var self = this;
      if (self.$route.params.id) {
        self.api
          .get(self.$route.params.id)
          .then((response) => {
            self.obj = response.result;
            self.claimItems = self.obj.items;

            // Set selectedClaimState to match the current state of the claim
            // self.selectedClaimState = self.obj.claimState;

            // var claimState ={
            //   value: self.obj.claimState,
            //   label: self.obj.claimStateDescription,
            // };
            // self.selectedClaimState = self.obj.claimState;
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't load the claim. ${helper.getErrorMessage(data)}`, "danger");
          });
      } else {
        self.obj = self.getEmptyObj();
      }
    },
    onSubmit() {
      var self = this;

      let haveError = false;
      self.computedClaimItems.forEach((item) => {
        if (!item.chartAccount || !item.chartAccount.id) {
          haveError = true;
        } else {
          item.chartAccountId = item.chartAccount.id;
        }
      });

      if (haveError) {
        self.showAccountErrors = true;
        return;
      }
      self.showAccountErrors = false;
      if (self.saving) return;
      self.saving = true;

      // self.obj.claimState = self.selectedClaimState;
      self.obj.items = self.computedClaimItems;

        if (!self.obj.id) {
          this.api
            .create(self.obj)
            .then((response) => {
              self.toast("Success", "Claim saved", "success");
              this.resetObj();
            })
            .catch(({ data }) => {
              self.toast("Error", `Couldn't save the claim. ${helper.getErrorMessage(data)}`, "danger");
            })
            .finally(() => {
              self.saving = false;
            });
        } else {
          this.api
            .update(self.obj)
            .then((response) => {
              // self.$router.push({ path: "/employee/claimList" });
              self.toast("Success", "Claim saved", "success");
              this.resetObj();
            })
            .catch(({ data }) => {
              self.toast("Error", `Couldn't save the claim. ${helper.getErrorMessage(data)}`, "danger");
            })
            .finally(() => {
              self.saving = false;
            });
        }
     
    },
    onReset() {
      this.obj = {};
    },
    toast(header, message, color) {
      const kind = { danger: "error", success: "success", warning: "warning" }[color] || "info";
      this.$toast[kind](message);
    },

    checkIfValid(fieldName) {
      const field = this.$v.obj[fieldName];
      if (!field.$dirty) {
        return null;
      }
      return !(field.$invalid || field.$model === "");
    },
    validate() {
      this.$v.$touch();
    },
    reset() {
      this.resetObj();
      this.submitted = false;
      this.$v.$reset();
    },
    getEmptyObj() {
      return {
        // code: "",
        // name: "",
        // decsription: "",
      };
    },
    submit() {
      this.onSubmit();
      this.submitted = true;
    },
  },
};
</script>

<style scoped>
.receipt-frame {
  width: 100%;
  height: 150px;
}
.receipt-img {
  max-width: 100%;
  max-height: 150px;
  object-fit: contain;
}
</style>
